import { ObjectId } from '@racemap/sdk/schema/base';
import {
  type EntityEvent,
  EntityEventState,
  EntityEventType,
  EntityType,
} from '@racemap/sdk/schema/entityEvents';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import {
  type PointWithTrackOffset,
  decodeBinaryBinarized,
} from '@racemap/utilities/functions/codec';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import {
  type ExtendedStarterTrack,
  type ExtendedStarterTrackPoint,
  type ExtendedStarterTracks,
  enrichTrackWithShadowDistances,
} from '@racemap/utilities/functions/timing';
import { calculateDistanceInMeter } from '@racemap/utilities/point_utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { LatLng } from '@racemap/utilities/types/trackping';
import * as Comlink from 'comlink';

interface StarterStateAtPoint {
  speed: number;
  avgSpeed: number;
  duration: number;
  noMoveDuration: number;
  distance: number;
  isBreak: boolean;
  offset: number;
  timeOffset: number;
  hasBeenOnceOnShadowTrack: boolean;
  isNonMovementSituation: boolean;
  noMovementSpeedThreshold: number;
  awayFromTrack: boolean;
  distanceToTrack: number;
  awayFromTrackDuration: number;
}

export interface RefinedPoint extends Omit<PointWithTrackOffset, 'time'> {
  time: Date;
  speed: number;
  timeOffset: number;
  isBreak: boolean;
}

export const pseudoFrontendUserId = new ObjectId('5f7b3b3b7f7b7b7b7f7b7b7b');

export class StarterTracksManagement {
  tracks: ExtendedStarterTracks = new Map();
  frontendEntityEvents: Map<string, EntityEvent> = new Map();
  isLoadingTracks = false;
  apiClient: RacemapAPIClient | null = null;
  event: RacemapEvent | null = null;
  onUpdateFinish: () => void = () => {};
  onReady: (() => Promise<void>) & Comlink.ProxyMarked = Comlink.proxy(async () => {});
  onUpdateStart: () => void = () => {};
  _eventId: ObjectId | null = null;
  _updateInterval: NodeJS.Timeout | null = null;

  constructor({
    eventId,
    host,
  }: {
    eventId: string;
    host: string;
  }) {
    console.log('Creating StarterTracksManagement for event', eventId);
    this.apiClient = new RacemapAPIClient(host);
    this._eventId = new ObjectId(eventId);
  }

  async init(): Promise<void> {
    this.onUpdateStart();
    await this._fetchEvent();
    await this._fetchStarterTracks();
    console.log(`${this.tracks.size} Starter tracks loaded`);
    this.onReady();
    this.onUpdateFinish();

    this._updateInterval = setInterval(() => {
      this.update();
    }, 10000);
  }

  terminate(): void {
    this.apiClient = null;
    this.tracks.clear();
    this.frontendEntityEvents.clear();
    this.isLoadingTracks = false;
    this.event = null;
    if (this._updateInterval != null) clearInterval(this._updateInterval);
  }

  async update(): Promise<void> {
    this.onUpdateStart();
    await this._fetchStarterTracks();
    console.log('Starter tracks updated');
    this.onUpdateFinish();
  }

  async getLastPoints(): Promise<Record<string, ExtendedStarterTrackPoint>> {
    const lastPoints: Record<string, ExtendedStarterTrackPoint> = {};

    for (const [trackerId, points] of this.tracks) {
      lastPoints[trackerId] = points[points.length - 1];
    }

    return lastPoints;
  }

  async getLastPointOfStarter(starterId: string): Promise<RefinedPoint | null> {
    const points = this.tracks.get(starterId);
    if (points == null || points.length === 0) return null;
    return points[points.length - 1];
  }

  async getStarterTrack(starterId: string): Promise<ExtendedStarterTrack> {
    return this.tracks.get(starterId) || [];
  }

  async getFrontendGeneratedEntityEvents(): Promise<Map<string, EntityEvent>> {
    return this.frontendEntityEvents;
  }

  async _fetchEvent(): Promise<void> {
    if (this._eventId == null) throw new Error('Event ID is not set');
    if (this.apiClient == null) throw new Error('API client is not initialized');

    this.event = await this.apiClient.getEvent(this._eventId.toHexString());
  }

  async _fetchStarterTracks(): Promise<void> {
    if (this.isLoadingTracks) {
      console.log('Starter tracks are already loading');
      return;
    }
    if (this.apiClient == null) throw new Error('API client is not initialized');
    if (this.event == null) throw new Error('Event is not loaded');

    try {
      console.log('Getting starter tracks for event', this.event.id);
      console.time('Fetched starter tracks');
      this.isLoadingTracks = true;
      const [startTime, endTime] = currentEventTimes(this.event);
      if (startTime == null || endTime == null) {
        console.error('Event times are not set');
        return;
      }

      const rawPoints = await this.apiClient.getEventPointsRaw(this.event.id, {
        offsetMode: 'binarized',
        range: `${new Date(startTime).toISOString()}--${new Date(endTime).toISOString()}`,
        clustered: false,
      });
      const tracksWithoutSpeed = decodeBinaryBinarized(rawPoints);
      const { pointsWithSpeed, frontendEntityEvents } =
        this._addSpeedToPointsAndCreateFrontendAlerts(tracksWithoutSpeed);
      const chunkedShadowtrack = await this.apiClient.getEventChunkedShadowGeo(
        this.event.id.toString(),
      );

      const tracksWithShadowDistances = this._enrichTrackWithShadowDistances(
        pointsWithSpeed,
        chunkedShadowtrack,
      );
      this.tracks = tracksWithShadowDistances;
      this.frontendEntityEvents = frontendEntityEvents;
      console.timeEnd('Fetched starter tracks');
    } catch (error) {
      console.error('Failed to get starter tracks', error);
    } finally {
      this.isLoadingTracks = false;
    }
  }

  /**
   * Calculates state information at a specific point on a track based on the current and previous points.
   *
   * @param options - The options object
   * @param options.currentPoint - The current point with offset information
   * @param options.previousPoint - The previous point with offset information
   * @returns An object containing:
   *   - awayFromTrack: Whether the current point is on a shadow track (offset >= 0)
   *   - speed: The calculated speed in km/h between points
   *   - duration: The time difference in milliseconds between points
   *   - noMoveDuration: The duration with no movement (always 0 in this implementation)
   *   - distance: The distance in meters between points
   * @private
   */
  _getStarterStateAtPoint({
    currentPoint,
    previousPoint,
    previousState,
    eventOptions,
  }: {
    currentPoint: PointWithTrackOffset;
    previousPoint: PointWithTrackOffset;
    previousState?: StarterStateAtPoint;
    eventOptions: {
      minSpeed: number;
      breakTimeout: number;
      noMovementThreshold: number;
    };
  }): StarterStateAtPoint {
    const { minSpeed, breakTimeout, noMovementThreshold } = eventOptions;
    const breaksArePossible = breakTimeout !== -1;
    const noMovementSpeedThreshold = Math.max(minSpeed, 1);
    const out: StarterStateAtPoint = {
      speed: 0,
      avgSpeed: 0,
      duration: 0,
      noMoveDuration: 0,
      distance: 0,
      isBreak: false,
      offset: currentPoint.offset * 10,
      timeOffset: 0,
      hasBeenOnceOnShadowTrack: false,
      isNonMovementSituation: false,
      noMovementSpeedThreshold,
      awayFromTrack: false,
      distanceToTrack: 0,
      awayFromTrackDuration: 0,
    };

    out.awayFromTrack = !currentPoint.isOnTrack;
    out.hasBeenOnceOnShadowTrack = !previousState?.awayFromTrack || !out.awayFromTrack;
    out.distance = calculateDistanceInMeter(
      previousPoint.lat,
      previousPoint.lng,
      currentPoint.lat,
      currentPoint.lng,
    );

    // duration between last two points in milliseconds
    out.duration = currentPoint.time.valueOf() - previousPoint.time.valueOf();
    out.timeOffset = out.hasBeenOnceOnShadowTrack
      ? (previousState?.timeOffset || 0) + out.duration
      : 0;
    out.speed = (out.distance * 3600) / out.duration;
    const alpha = 0.2;
    out.avgSpeed = alpha * out.speed + (1 - alpha) * (previousState?.avgSpeed || 0);

    if (breaksArePossible && out.duration / 1000 > breakTimeout) {
      out.speed = 0;
      out.isBreak = true;
    }

    if (out.avgSpeed < noMovementSpeedThreshold || previousState?.isNonMovementSituation) {
      out.noMoveDuration = (previousState?.noMoveDuration || 0) + out.duration;
    }

    if (
      noMovementThreshold &&
      out.noMoveDuration > noMovementThreshold &&
      out.avgSpeed < noMovementSpeedThreshold
    ) {
      out.isNonMovementSituation = true;
    }

    if (out.awayFromTrack || previousState?.awayFromTrack) {
      out.awayFromTrackDuration = (previousState?.awayFromTrackDuration || 0) + out.duration;
    }

    return out;
  }

  _calculateOccurredAt(point: PointWithTrackOffset, duration: number): Date {
    return new Date(point.time.valueOf() - duration);
  }

  _createAlert({
    eventType,
    entityType,
    point,
    duration,
    event,
    starterId,
    information,
    solved = false,
  }: {
    eventType: EntityEventType;
    entityType: EntityType;
    point: PointWithTrackOffset;
    duration: number;
    event: RacemapEvent;
    starterId: ObjectId | string;
    information: string;
    solved: boolean;
  }): EntityEvent {
    const occurredAt = this._calculateOccurredAt(point, duration);

    return {
      id: new ObjectId(),
      entityType,
      eventType,
      entityId: new ObjectId(starterId),
      occurredAt,
      creatorId: new ObjectId(event.creatorId),
      updaterId: new ObjectId(event.id),
      createdAt: new Date(point.time),
      updatedAt: new Date(point.time),
      stateEditedAt: solved ? new Date(point.time) : null,
      stateEditedBy: solved ? pseudoFrontendUserId : null,
      location: {
        lat: point.lat,
        lng: point.lng,
      },
      information: information,
      state: solved ? EntityEventState.RESOLVED : EntityEventState.NEW,
    };
  }

  _addSpeedToPointsAndCreateFrontendAlerts(
    tracksWithoutSpeed: Map<string, Array<PointWithTrackOffset>>,
  ): {
    pointsWithSpeed: Map<string, Array<RefinedPoint>>;
    frontendEntityEvents: Map<string, EntityEvent>;
  } {
    if (this.event == null) throw new Error('Event is not loaded');

    // TODO: change function to generate new tracks and make no inplace modification
    // Calculate results for each track (starter)
    // We are accumulating the time and the offset each starter collects while moving along its own track
    // If there is a gap longer then event.breakTimeout we remove this delatTime and deltaOffset from collection
    const {
      playerOptions: { breakTimeout },
      minSpeed,
      alertOptions: { noMovementDuration, autoResolveAlerts, generateGeoBasedNoMovementAlerts },
    } = this.event;
    const tracksWithSpeed: Map<string, Array<RefinedPoint>> = new Map();
    const entityEvents = new Map<string, EntityEvent>();

    for (const [starterId, track] of tracksWithoutSpeed.entries()) {
      // here we are just accumulating all results and offsest
      // we have to multiply by 10 cause offsets from the server are devided by 10
      // negative offset indicates the point was not on the shadow track
      const trackWithSpeed: Array<RefinedPoint> = [];
      let previousState: StarterStateAtPoint | undefined;

      for (const [index, point] of track.entries()) {
        if (point.time.valueOf() > Date.now()) continue;

        const newPoint: RefinedPoint = {
          ...point,
          time: new Date(point.time),
          offset: 0,
          timeOffset: 0,
          speed: 0,
          isBreak: false,
        };

        if (index === 0) {
          newPoint.offset = 0;
          newPoint.timeOffset = 0;
          newPoint.speed = 0;

          trackWithSpeed.push(newPoint);
          continue;
        }
        const currentState = this._getStarterStateAtPoint({
          currentPoint: point,
          previousPoint: track[index - 1],
          previousState,
          eventOptions: { minSpeed, breakTimeout, noMovementThreshold: noMovementDuration },
        });
        const {
          speed,
          offset,
          timeOffset,
          isNonMovementSituation,
          noMoveDuration,
          noMovementSpeedThreshold,
          avgSpeed,
          awayFromTrack,
          awayFromTrackDuration,
        } = currentState;

        newPoint.offset = offset;
        newPoint.speed = speed;
        newPoint.timeOffset = timeOffset;

        trackWithSpeed.push(newPoint);

        // check for await from track or non movement situation
        if (
          generateGeoBasedNoMovementAlerts &&
          (isNonMovementSituation || previousState?.isNonMovementSituation) &&
          index < track.length - 1
        ) {
          const solved =
            avgSpeed > noMovementSpeedThreshold &&
            autoResolveAlerts.includes(EntityEventType.NO_MOVEMENT);

          const alert = this._createAlert({
            eventType: EntityEventType.NO_MOVEMENT,
            entityType: EntityType.STARTERS,
            point,
            duration: noMoveDuration,
            event: this.event,
            starterId,
            information: `${noMoveDuration / 60000}min with a average speed < ${noMovementSpeedThreshold}km/h`,
            solved,
          });
          const alertKey = `${starterId}_${alert.eventType}_${alert.occurredAt.toISOString()}`;

          entityEvents.set(alertKey, alert);
        }

        if ((awayFromTrack || previousState?.awayFromTrack) && index < track.length - 1) {
          const occurredAt = this._calculateOccurredAt(point, awayFromTrackDuration);
          const alertKey = `${starterId}_${EntityEventType.AWAY_FROM_TRACK}_${occurredAt.toISOString()}`;
          const existingAlert = entityEvents.get(alertKey);
          const solved =
            !awayFromTrack && autoResolveAlerts.includes(EntityEventType.AWAY_FROM_TRACK);

          if (existingAlert) {
            existingAlert.occurredAt = occurredAt;
            existingAlert.updatedAt = new Date(point.time);
            existingAlert.information = `${(awayFromTrackDuration / 60000).toFixed(1)}min away from track`;
            existingAlert.state = solved ? EntityEventState.RESOLVED : existingAlert.state;
            existingAlert.stateEditedAt = solved ? new Date(point.time) : null;
            existingAlert.stateEditedBy = solved ? pseudoFrontendUserId : null;
          } else {
            const alert = this._createAlert({
              eventType: EntityEventType.AWAY_FROM_TRACK,
              entityType: EntityType.STARTERS,
              point,
              duration: awayFromTrackDuration,
              event: this.event,
              starterId,
              information: `${(awayFromTrackDuration / 60000).toFixed(1)}min away from track`,
              solved,
            });

            entityEvents.set(alertKey, alert);
          }
        }

        // update the previous state
        previousState = currentState;
      }

      tracksWithSpeed.set(starterId, trackWithSpeed);
    }

    return { pointsWithSpeed: tracksWithSpeed, frontendEntityEvents: entityEvents };
  }

  _enrichTrackWithShadowDistances(
    track: Map<string, Array<RefinedPoint>>,
    shadowTrack?: Array<LatLng> | null,
  ): ExtendedStarterTracks {
    return enrichTrackWithShadowDistances(track, shadowTrack);
  }
}

Comlink.expose(StarterTracksManagement);
